import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import SearchIcon from "../../SearchIcon/SearchIcon";
import { getDeviceExtraInfoValuesByKey } from "../../../shared/db/devices-db";
import { getPeopleExtraInfoValuesByKey } from "../../../shared/db/people-db";
import { ItemType } from '../../../shared/interfaces';
import { Actions as GeneralActions } from '../../../stores/reducers/general-reducers';
import styles from './FieldSuggestions.module.scss';

interface FieldSuggestionsProps {
  idx: string;
  readOnly: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChangeHandler: (v: string) => void;
  value: string;
  type: ItemType;
  activeIdx: string | null;
  updateActiveIdx: (idx: string | null) => void;
  allDeviceIds: string[];
  allPeopleIds: string[];
  hideSuggestions?: boolean;
  kind?: string;
}

const LIMIT = 20;
const DEBOUNCE_DELAY = 150;

const naturalSort = (a: string, b: string, order = 'asc') => {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const cleanedA = a?.trim().replace(/\s+/g, ' ') || '';
  const cleanedB = b?.trim().replace(/\s+/g, ' ') || '';
  const comparison = collator.compare(cleanedA, cleanedB);
  return order === 'asc' ? comparison : -comparison;
};

const compareByNameAscending = (a: string, b: string) => naturalSort(a, b, 'asc');

const normalizeText = (str: string) =>
  str.trim().replace(/\s+/g, ' ').toLowerCase();

const uniqueSorted = (arr: string[]) => {
  const normalizedSet = new Set<string>();
  const uniqueItems: string[] = [];

  for (const item of arr) {
    const normalized = normalizeText(item);
    if (!normalizedSet.has(normalized)) {
      normalizedSet.add(normalized);
      uniqueItems.push(item);
    }
  }

  return uniqueItems.sort(compareByNameAscending);
};

const filterSuggestions = (list: string[], keyword: string) => {
  const normalizedKeyword = normalizeText(keyword);
  if (!normalizedKeyword) {
    return uniqueSorted(list);
  }

  const filtered = list.filter(item =>
    normalizeText(item).includes(normalizedKeyword)
  );

  return uniqueSorted(filtered);
};

const FieldSuggestions: React.FC<FieldSuggestionsProps> = ({
  idx,
  readOnly,
  onChange,
  onChangeHandler,
  value,
  type,
  activeIdx,
  updateActiveIdx,
  allDeviceIds,
  allPeopleIds,
  hideSuggestions = false,
  kind = 'select'
}) => {
  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const initialValueRef = useRef<string>('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchIdRef = useRef(0);

  const dispatch = useDispatch();
  const canShowSuggestions = kind === 'select' || kind === 'legacy';

  const applyFilterForValue = useCallback((suggestions: string[], currentValue: string) => {
    const normalizedInput = normalizeText(currentValue);
    const normalizedInitial = normalizeText(initialValueRef.current);

    const isSameAsInitial = normalizedInput === normalizedInitial;
    const isEmpty = normalizedInput === '';

    if (isSameAsInitial || isEmpty) {
      setFilteredSuggestions(suggestions);
      return;
    }

    setFilteredSuggestions(filterSuggestions(suggestions, currentValue));
  }, []);

  const handleFocus = useCallback(async () => {
    if (activeIdx === idx || isLoading || !canShowSuggestions) return;

    const fetchId = ++fetchIdRef.current;

    setIsLoading(true);
    updateActiveIdx(idx);

    initialValueRef.current = value;

    let possibleValues: string[] = [];

    try {
      if (type === 'device') {
        possibleValues = await getDeviceExtraInfoValuesByKey(allDeviceIds, idx);
      } else if (type === 'person') {
        possibleValues = await getPeopleExtraInfoValuesByKey(allPeopleIds, idx, LIMIT);
      }
    } finally {
      // no-op: loading handled below
    }

    if (fetchId !== fetchIdRef.current) return;

    const uniqueValues = uniqueSorted(possibleValues);
    setAllSuggestions(uniqueValues);

    applyFilterForValue(uniqueValues, value);

    setIsLoading(false);
  }, [
    activeIdx,
    idx,
    isLoading,
    updateActiveIdx,
    value,
    type,
    allDeviceIds,
    allPeopleIds,
    applyFilterForValue,
    canShowSuggestions
  ]);

  const debouncedFilter = useCallback((newVal: string, suggestions: string[]) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      applyFilterForValue(suggestions, newVal);
    }, DEBOUNCE_DELAY);
  }, [applyFilterForValue]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeIdx !== idx) {
      setActiveSuggestion(null);
    }
  }, [activeIdx, idx]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newVal = e.target.value;

    if (kind === 'number' || kind === 'money') {
        newVal = newVal.replace(/[^0-9.,-]/g, '');
        e.target.value = newVal;
    }

    onChange(e);
    if (canShowSuggestions) {
        debouncedFilter(newVal, allSuggestions);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    onChangeHandler(suggestion);
    updateActiveIdx(null);
  };

  const handleSearchButtonClick = (e: React.MouseEvent, searchTerm: string) => {
    e.stopPropagation();

    dispatch(GeneralActions.SHOW_SEARCH);
    dispatch(GeneralActions.CHANGE_TERM(searchTerm));
  };

  const renderInput = () => {
      if (kind === 'textarea') {
          return (
              <textarea
                  ref={textAreaRef}
                  disabled={readOnly}
                  className={styles.FieldInput}
                  value={value}
                  onChange={handleInputChange}
                  style={{ resize: 'vertical', minHeight: '60px', width: '100%' }}
              />
          );
      }

        if (kind === 'number' || kind === 'money') {
          return (
              <input
                  ref={inputRef}
                  type="text"
                  inputMode="decimal"
                  disabled={readOnly}
                  className={styles.FieldInput}
                  value={value}
                  onChange={handleInputChange}
              />
          );
      }

      return (
          <input
              ref={inputRef}
              type="text"
              disabled={readOnly}
              className={styles.FieldInput}
              value={value}
              onChange={handleInputChange}
              onFocus={handleFocus}
          />
      );
  };

  const shouldShowSuggestions = canShowSuggestions && !hideSuggestions && activeIdx === idx && !isLoading;

  return (
    <div className={styles.FieldSuggestions}>
      {renderInput()}

      {shouldShowSuggestions && (
        <div className={styles.Suggestions}>
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion, index) => (
              <div
                key={`${suggestion}-${index}`}
                className={styles.Suggestion}
                onMouseDown={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setActiveSuggestion(suggestion)}
              >
                <span>{suggestion}</span>
                <div className={styles.actionButtons}>
                  <button
                    className={styles.searchButton}
                    type="button"
                    onMouseDown={(e) => handleSearchButtonClick(e, suggestion)}
                  >
                    <SearchIcon viewBox="0 0 18 10" />
                  </button>
                  <svg
                    className={styles.checkSVG}
                    style={{ display: activeSuggestion === suggestion ? 'block' : 'none' }}
                  >
                    <use xlinkHref="/assets/svg/check.svg#check" />
                  </svg>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noSuggestions}>No suggestions found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FieldSuggestions;
